import { Component, OnInit, OnDestroy, ViewEncapsulation, ChangeDetectionStrategy, NgZone } from '@angular/core';
import { AppState } from '../../../reducers';
import { select, Store } from '@ngrx/store';
import { Subscription, combineLatest, timer, fromEvent, BehaviorSubject, interval } from 'rxjs';
import { map, filter, switchMap, take, startWith } from 'rxjs/operators';
import {
  selectAuthTokenExpiresAt,
  selectAuthTokenExpiresIn,
  selectAuthLoggedIn,
  selectAuthLifetime
} from '../auth.selectors';
import { RefreshTokenAction, LogoutAction, GetUserAction } from '../auth.actions';
import { ActivationStart, Router } from '@angular/router';
import { AuthService } from '../auth.service';
import { LogoutService } from '../../../shared/logout/logout.service';
import { get } from 'lodash';
import { environment } from '../../../../environments/environment';

@Component({
  standalone: false,
  selector: 'dsp-session',
  template: ``,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SessionComponent implements OnInit, OnDestroy {
  private readonly subscription: Subscription = new Subscription();

  private readonly _tokenTimerSubscription: Subscription = new Subscription();
  private readonly _userSubscription: Subscription = new Subscription();
  private readonly _routeSubscription: Subscription = new Subscription();
  public isUserLogged$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public constructor(
    private readonly store: Store<AppState>,
    private readonly _store: Store<AppState>,
    private readonly _authService: AuthService,
    private readonly _router: Router,
    private readonly _zone: NgZone,
    private readonly _logoutService: LogoutService,

  ) {
  }

  public ngOnInit(): void {
    this._routeSubscription.add(this.subscribeRoute());

    // this.subscription.add(this.subscribeOnLoad());
    // this.subscription.add(this.subscribeRefreshToken());
    this.subscription.add(this.subscribeLogoutInactive());
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
    this._tokenTimerSubscription.unsubscribe();
    this._userSubscription.unsubscribe();
  }

  private subscribeOnLoad(): Subscription {
    return this.store.pipe(
      select(selectAuthLoggedIn),
      filter(loggedIn => !!loggedIn),
      take(1)
    ).subscribe(() => {
      this.store.dispatch(new RefreshTokenAction());
    });

  }

  private subscribeRefreshToken(): Subscription {
    // return combineLatest(
    //   this.store.pipe(select(selectAuthLoggedIn)),
    //   this.store.pipe(select(selectAuthTokenExpiresIn)),
    //   this.store.pipe(select(selectAuthTokenExpiresAt))
    // ).pipe(
    return combineLatest([
      this.store.pipe(select(selectAuthLoggedIn)),
      this.store.pipe(select(selectAuthTokenExpiresIn)),
      this.store.pipe(select(selectAuthTokenExpiresAt))
    ]).pipe(
      switchMap(([loggedIn, expiresIn]) => timer(expiresIn).pipe(filter(() => !!loggedIn)))
    ).subscribe(() => {
      this.store.dispatch(new RefreshTokenAction());
    });
  }

  private subscribeLogoutInactive(): Subscription {
    return combineLatest([
      this._authService.fullyAuthenticated$,
      fromEvent(document, 'click').pipe(startWith())
    ]).pipe(
      switchMap(([fullyAuthenticated]) =>
        timer((+environment.refreshTokenExpiresIn * 1000)).pipe(filter(() => !!fullyAuthenticated))
      ),
    ).subscribe(() => {
      this._logoutService._continue();
    });
  }

  private setCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
    document.cookie = name + '=' + value + ';path=/;secure;expires=' + d.toUTCString();
  }

  private deleteCookie(name) {
    this.setCookie(name, '', -1);
  }

  private deleteCookies() {
    const allCookies = document.cookie.split(';');
    for (let i = 0; i < allCookies.length; i++) {
      this.deleteCookie(allCookies[i]);
    }
  }

  private subscribeRoute(): Subscription {
    return this._router.events.pipe(filter(e => e instanceof ActivationStart)).subscribe(router => {
      const path = get(router, ['snapshot', 'routeConfig', 'path']);
      if (path !== 'login-verify') {
        this._authService.mssoAuthService.checkSession();
        this._userSubscription.add(this.subscribeIsUserLoggedIn());
        this._userSubscription.add(this.subscribeUserActions());
        // this._versionService.outputVersions();
        this._routeSubscription.unsubscribe();
      }
    });
  }

  private subscribeUserActions(): Subscription {
    return combineLatest([this._authService.fullyAuthenticated$]) // change
      .pipe(filter(([fullyAuthenticated]) => !!fullyAuthenticated))// change
      .subscribe(() => {
        this.enableRefreshTokenTimer();
      });
  }


  private subscribeIsUserLoggedIn(): Subscription {
    return this._authService.fullyAuthenticated$.subscribe(authenticated => {
      if (authenticated) {
        this._store.dispatch(new GetUserAction());
        this.setSessionAsActive();
        // this.handleGetUserInfoRequest();
      } else {
        if (this.isUserLogged$.value) {
          this.setSessionAsInactive();
        }
      }
    });
  }

  private setSessionAsActive(): void {
    this.isUserLogged$.next(true);
    this.enableRefreshTokenTimer();
    // this._store.dispatch(new ResetActivityAction());
  }

  private enableRefreshTokenTimer(): void {
    if (this._tokenTimerSubscription) {
      this._tokenTimerSubscription.unsubscribe();
    }
    // this._zone.runOutsideAngular(() => {
    //   this._tokenTimerSubscription = interval((+environment.refreshTokenExpiresIn || 1200) * 1000).subscribe(() =>
    //     this._zone.run(() => {
    //       // this._logoutService._continue();
    //     }),
    //   );
    // });
  }

  private setSessionAsInactive(): void {
    this.isUserLogged$.next(false);
    this.disableRefreshTokenTimer();
    this._logoutService._continue();
  }

  private disableRefreshTokenTimer(): void {
    if (this._tokenTimerSubscription) {
      this._tokenTimerSubscription.unsubscribe();
    }
  }
}
